import { Component, Inject, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as L from 'leaflet';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';

interface Waypoint {
  lat: number;
  lon: number;
  isNoon?: boolean;
  isTurningPoint?: boolean;
  isLocked?: boolean;
}

interface LegData {
  legName: string;
  route_id: string;
  routeJson: any;
  waypoints: Waypoint[];
  color: string;
  polylineLayer?: L.Polyline;
  markerLayers: L.Marker[];
  originalSeg: any;
}

@Component({
  selector: 'app-route-map-modal',
  templateUrl: './route-map-modal.component.html',
  styleUrls: ['./route-map-modal.component.scss']
})
export class RouteMapModalComponent implements OnInit, AfterViewInit {
  map!: L.Map;
  isSaving = false;
  isRecalculating = false;
  
  legDataArray: LegData[] = [];
  
  showDistances = false;
  showCourse = false;
  
  getGCDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
      const R = 3440.065;
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
          Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
  }

  getRLDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
      const R = 3440.065;
      const lat1Rad = lat1 * Math.PI / 180;
      const lat2Rad = lat2 * Math.PI / 180;
      const lon1Rad = lon1 * Math.PI / 180;
      const lon2Rad = lon2 * Math.PI / 180;

      const dLat = lat2Rad - lat1Rad;
      let dLon = lon2Rad - lon1Rad;

      if (Math.abs(dLon) > Math.PI) {
          dLon = dLon > 0 ? -(2 * Math.PI - dLon) : (2 * Math.PI + dLon);
      }

      const dPhi = Math.log(Math.tan(Math.PI / 4 + lat2Rad / 2) / Math.tan(Math.PI / 4 + lat1Rad / 2));
      const q = Math.abs(dPhi) > 1e-10 ? dLat / dPhi : Math.cos(lat1Rad);

      const distRad = Math.sqrt(dLat * dLat + q * q * dLon * dLon);
      return R * distRad;
  }

  getBearing(lat1: number, lon1: number, lat2: number, lon2: number) {
      const lat1Rad = lat1 * Math.PI / 180;
      const lat2Rad = lat2 * Math.PI / 180;
      const lon1Rad = lon1 * Math.PI / 180;
      const lon2Rad = lon2 * Math.PI / 180;

      const dLon = lon2Rad - lon1Rad;
      const y = Math.sin(dLon) * Math.cos(lat2Rad);
      const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) -
          Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);
      const brng = Math.atan2(y, x) * 180 / Math.PI;
      return (Math.round(brng) + 360) % 360;
  }

  getPathFractionalPoint(coords: any[], fraction: number) {
      if (coords.length === 0) return { point: null, index: 0 };
      if (coords.length === 1) return { point: coords[0], index: 0 };

      let distances = [0];
      let totalDist = 0;
      for (let i = 0; i < coords.length - 1; i++) {
          let d = this.getGCDistance(coords[i][1], coords[i][0], coords[i + 1][1], coords[i + 1][0]);
          totalDist += d;
          distances.push(totalDist);
      }

      if (totalDist === 0) return { point: coords[0], index: 0 };

      let targetDist = totalDist * fraction;
      for (let i = 0; i < coords.length - 1; i++) {
          if (targetDist >= distances[i] && targetDist <= distances[i + 1]) {
              let segDist = distances[i + 1] - distances[i];
              let segFraction = segDist === 0 ? 0 : (targetDist - distances[i]) / segDist;
              let pt1 = coords[i];
              let pt2 = coords[i + 1];
              let lon = pt1[0] + (pt2[0] - pt1[0]) * segFraction;
              let lat = pt1[1] + (pt2[1] - pt1[1]) * segFraction;
              return { point: [lon, lat], index: i };
          }
      }
      return { point: coords[coords.length - 1], index: coords.length - 2 };
  }
  
  // Custom icons
  addedIcon = L.divIcon({
    className: 'custom-added-marker',
    html: `<div style="background-color: #fff; border: 3px solid #000; width: 14px; height: 14px; border-radius: 50%;"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7]
  });
  
  noonIcon = L.divIcon({
    className: 'custom-noon-marker',
    html: `<div style="background-color: #ef4444; border: 3px solid #000; width: 14px; height: 14px; border-radius: 50%;"></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7]
  });

  midIcon = L.divIcon({
    className: 'custom-mid-marker',
    html: `<div style="background-color: #fff; border: 2px solid #666; width: 10px; height: 10px; border-radius: 50%; opacity: 0.8;"></div>`,
    iconSize: [10, 10], iconAnchor: [5, 5]
  });

  lockedIcon = L.divIcon({
    className: 'custom-locked-marker',
    html: `<div style="background-color: #6b7280; border: 3px solid #374151; width: 14px; height: 14px; border-radius: 50%; display: flex; align-items: center; justify-content: center;"><span style="font-size: 8px; color: white;">🔒</span></div>`,
    iconSize: [14, 14], iconAnchor: [7, 7]
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<RouteMapModalComponent>,
    private requestFormService: RequestFormService,
    private cdr: ChangeDetectorRef
  ) {
    this.parseInputData(data.routes || {});
  }
  
  ngOnInit() {}
  
  ngAfterViewInit() {
    this.initMap();
    this.drawAllLegs(true);
  }
  
  parseInputData(routesObj: any) {
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];
    let colorIndex = 0;
    
    for (const leg of Object.keys(routesObj)) {
      const segments = routesObj[leg];
      for (const seg of segments) {
        if (seg.route) {
          let originalNoons = seg.waypoints ? JSON.parse(JSON.stringify(seg.waypoints)) : [];
          
          let routeCoords: any[] = [];
          try {
            const flat = seg.route.features[0].geometry.coordinates;
            routeCoords = Array.isArray(flat[0][0]) ? flat[0] : flat;
          } catch (e) {}

          let allWaypoints: Waypoint[] = [];
          
          if (routeCoords.length > 0) {
              let isCurrentlyLocked = false;
              if (originalNoons.length > 0 && originalNoons[0].isLocked && (originalNoons.length > 1 ? originalNoons[1].isLocked : true)) {
                  isCurrentlyLocked = true;
              }

              for (const c of routeCoords) {
                  let isNoon = false;
                  let isLocked = isCurrentlyLocked;

                  let matchedNoonIndex = -1;
                  for (let i = 0; i < originalNoons.length; i++) {
                      const noon = originalNoons[i];
                      if (Math.abs(noon.lon - c[0]) < 0.01 && Math.abs(noon.lat - c[1]) < 0.01) {
                          isNoon = true;
                          matchedNoonIndex = i;
                          isLocked = noon.isLocked;
                          break;
                      }
                  }

                  if (matchedNoonIndex !== -1) {
                      const nextNoonLocked = (matchedNoonIndex + 1 < originalNoons.length) ? originalNoons[matchedNoonIndex + 1].isLocked : originalNoons[matchedNoonIndex].isLocked;
                      isCurrentlyLocked = originalNoons[matchedNoonIndex].isLocked && nextNoonLocked;
                  }

                  allWaypoints.push({
                      lon: c[0],
                      lat: c[1],
                      isNoon: isNoon,
                      isLocked: isLocked
                  });
              }
          } else {
              allWaypoints = originalNoons.map((w: any) => ({ ...w, isNoon: true }));
          }

          this.legDataArray.push({
            legName: leg,
            route_id: seg.route_id,
            routeJson: seg.route,
            waypoints: allWaypoints,
            color: colors[colorIndex % colors.length],
            markerLayers: [],
            originalSeg: seg
          });
        }
      }
      colorIndex++;
    }
  }
  
  hasEditableWaypoints(): boolean {
    for (const leg of this.legDataArray) {
      for (const wp of leg.waypoints) {
        if (!wp.isLocked) {
          return true; // Found at least one editable waypoint
        }
      }
    }
    return false;
  }
  
  initMap() {
    this.map = L.map('route-preview-map').setView([20, 45], 4);
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
      {
        maxNativeZoom: 16,
        maxZoom: 19
      }
    ).addTo(this.map);
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
      {
        maxNativeZoom: 16,
        maxZoom: 19
      }
    ).addTo(this.map);
  }


  
  drawAllLegs(fitBounds = false) {
    let bounds = L.latLngBounds([]);
    
    for (let i = 0; i < this.legDataArray.length; i++) {
      const legData = this.legDataArray[i];
      
      // Cleanup old layers
      if (legData.polylineLayer) {
        this.map.removeLayer(legData.polylineLayer);
      }
      legData.markerLayers.forEach(m => this.map.removeLayer(m));
      legData.markerLayers = [];
      
      // Draw detailed route polyline
      let routeCoords: any[] = [];
      try {
        const flat = legData.routeJson.features[0].geometry.coordinates;
        routeCoords = Array.isArray(flat[0][0]) ? flat[0] : flat;
      } catch (e) {}
      
      if (routeCoords.length > 0) {
        const latlngs = routeCoords.map((c: any) => [c[1], c[0]] as [number, number]);
        legData.polylineLayer = L.polyline(latlngs, {
          color: legData.color, weight: 5, opacity: 0.8, className: 'clickable-polyline'
        }).addTo(this.map);
        
        bounds.extend(legData.polylineLayer.getBounds());
        
        // Add click to add waypoint
        legData.polylineLayer.on('click', (e: L.LeafletMouseEvent) => {
          L.DomEvent.stopPropagation(e);
          // Only add waypoint if not clicking on a fully locked leg, but since it's waypoint based we can allow it
          // unless we want to disable adding waypoints between locked points. For now just allow adding but they are unlocked.
          this.addWaypointToLeg(i, e.latlng);
        });
      }
      
      // Draw waypoints and turning points
      // Draw waypoints
      for (let w = 0; w < legData.waypoints.length; w++) {
        const wp = legData.waypoints[w];
        
        let markerIcon = wp.isLocked ? this.lockedIcon : (wp.isNoon ? this.noonIcon : this.addedIcon);
        let opacity = 1;
        let draggable = !wp.isNoon && !wp.isLocked;
        
        const marker = L.marker([wp.lat, wp.lon], {
          icon: markerIcon,
          draggable: draggable,
          opacity: opacity
        }).addTo(this.map);
        
        let tooltipText = `[#${w+1}] ${wp.isNoon ? 'Actual Position (Noon)' : 'Derived Waypoint'}`;
        if (wp.isLocked) tooltipText += ' 🔒 (Locked)';
        marker.bindTooltip(tooltipText);
        
        if (draggable) {
          marker.on('drag', (e: any) => {
            const newPos = e.target.getLatLng();
            wp.lat = newPos.lat;
            wp.lon = newPos.lng;
            this.updateLegLocally(i);
          });
          
          marker.on('dragend', () => {
            this.drawAllLegs(false);
          });
        }
        
        marker.on('contextmenu', (e: any) => {
          e.originalEvent.preventDefault();
          if (!wp.isNoon && !wp.isLocked) {
            legData.waypoints.splice(w, 1);
            this.updateLegLocally(i);
            this.drawAllLegs(false);
          }
        });
        
        marker.on('click', () => {
          const targetId = `wp-card-${i}-${w}`;
          const element = document.getElementById(targetId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('highlight-card');
            setTimeout(() => element.classList.remove('highlight-card'), 1500);
          }
        });
        
        legData.markerLayers.push(marker);
        bounds.extend(marker.getLatLng());
      }
      
      // Premium Map Labels (Distances, Courses, Midpoints)
      if (routeCoords && routeCoords.length >= 2) {
        let macroSegments: any[] = [];
        let currentSegment = {
            startIndex: 0,
            endIndex: 1,
            coords: [routeCoords[0], routeCoords[1]],
            bearings: [] as number[]
        };

        let initialBearing = this.getBearing(routeCoords[0][1], routeCoords[0][0], routeCoords[1][1], routeCoords[1][0]);
        currentSegment.bearings.push(initialBearing);

        for (let j = 1; j < routeCoords.length - 1; j++) {
            let pt1 = routeCoords[j];
            let pt2 = routeCoords[j + 1];
            let bearing = this.getBearing(pt1[1], pt1[0], pt2[1], pt2[0]);

            let prevBearing = currentSegment.bearings[currentSegment.bearings.length - 1];
            let diff = Math.abs(bearing - prevBearing);
            if (diff > 180) diff = 360 - diff;

            if (diff <= 2.0) {
                currentSegment.endIndex = j + 1;
                currentSegment.coords.push(pt2);
                currentSegment.bearings.push(bearing);
            } else {
                macroSegments.push(currentSegment);
                currentSegment = {
                    startIndex: j,
                    endIndex: j + 1,
                    coords: [pt1, pt2],
                    bearings: [bearing]
                };
            }
        }
        macroSegments.push(currentSegment);

        macroSegments.forEach((segment) => {
            let segGCDist = 0;
            let segRLDist = 0;
            for (let j = 0; j < segment.coords.length - 1; j++) {
                let pt1 = segment.coords[j];
                let pt2 = segment.coords[j + 1];
                segGCDist += this.getGCDistance(pt1[1], pt1[0], pt2[1], pt2[0]);
                segRLDist += this.getRLDistance(pt1[1], pt1[0], pt2[1], pt2[0]);
            }

            let labelPointObj = this.getPathFractionalPoint(segment.coords, 0.25);
            let labelPoint = labelPointObj.point;

            if (this.showDistances && labelPoint) {
                const labelHtml = `
                    <div class="distance-label gc">GC: ${segGCDist.toFixed(1)} NM</div>
                    <div class="distance-label rl">RL: ${segRLDist.toFixed(1)} NM</div>
                `;
                const distMarker = L.marker([labelPoint[1], labelPoint[0]], {
                    icon: L.divIcon({
                        className: 'distance-label-container',
                        html: labelHtml,
                        iconAnchor: [65, 38]
                    }),
                    interactive: false
                }).addTo(this.map);
                legData.markerLayers.push(distMarker);
            }
        });

        if (this.showCourse) {
            routeCoords.forEach((coord, idx) => {
                if (idx < routeCoords.length - 1) {
                    const nextCoord = routeCoords[idx + 1];
                    const course = this.getBearing(coord[1], coord[0], nextCoord[1], nextCoord[0]);

                    const courseHtml = `
                        <div class="course-label" style="pointer-events: none;">
                            <span style="display: inline-block; transform: rotate(${course}deg); color: #38bdf8; margin-right: 3px; font-size: 8px;">↑</span>
                            ${course}°
                        </div>
                    `;
                    const courseMarker = L.marker([coord[1], coord[0]], {
                        icon: L.divIcon({
                            className: 'distance-label-container',
                            html: courseHtml,
                            iconAnchor: [-10, 18]
                        }),
                        interactive: false
                    }).addTo(this.map);
                    legData.markerLayers.push(courseMarker);
                }
            });
        }
      }
    }
    
    if (fitBounds && bounds.isValid()) {
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }
  }
  
  snapToPolyline(latlng: L.LatLng, routeCoords: any[]): L.LatLng {
    let minDistance = Infinity;
    let closestPoint = latlng;
    const pt = this.map.project(latlng);
    
    for (let i = 0; i < routeCoords.length - 1; i++) {
      // routeCoords is [lon, lat]
      const p1 = this.map.project(L.latLng(routeCoords[i][1], routeCoords[i][0]));
      const p2 = this.map.project(L.latLng(routeCoords[i+1][1], routeCoords[i+1][0]));
      
      const dist = L.LineUtil.pointToSegmentDistance(pt, p1, p2);
      if (dist < minDistance) {
        minDistance = dist;
        const closest = L.LineUtil.closestPointOnSegment(pt, p1, p2);
        closestPoint = this.map.unproject(closest);
      }
    }
    return closestPoint as L.LatLng;
  }
  
  addWaypointToLeg(legIndex: number, latlng: L.LatLng) {
    const legData = this.legDataArray[legIndex];
    if (legData.waypoints.length < 2) return;
    
    let minDistance = Infinity;
    let insertIndex = 1;
    
    const clickedPoint = this.map.project(latlng);
    
    for (let i = 0; i < legData.waypoints.length - 1; i++) {
      const wp1 = this.map.project(L.latLng(legData.waypoints[i].lat, legData.waypoints[i].lon));
      const wp2 = this.map.project(L.latLng(legData.waypoints[i+1].lat, legData.waypoints[i+1].lon));
      
      const dist = L.LineUtil.pointToSegmentDistance(clickedPoint, wp1, wp2);
      if (dist < minDistance) {
        minDistance = dist;
        insertIndex = i + 1;
      }
    }
    
    legData.waypoints.splice(insertIndex, 0, { lat: latlng.lat, lon: latlng.lng, isNoon: false });
    this.updateLegLocally(legIndex);
    this.drawAllLegs(false);
  }

  addWaypointBetween(legIndex: number, w: number) {
    const legData = this.legDataArray[legIndex];
    const wp1 = legData.waypoints[w];
    const wp2 = legData.waypoints[w + 1];
    const midLat = (wp1.lat + wp2.lat) / 2;
    const midLon = (wp1.lon + wp2.lon) / 2;
    legData.waypoints.splice(w + 1, 0, { lat: Number(midLat.toFixed(4)), lon: Number(midLon.toFixed(4)), isNoon: false });
    this.updateLegLocally(legIndex);
    this.drawAllLegs(false);
  }
  
  updateLegLocally(legIndex: number, redrawMarkers = false) {
    const legData = this.legDataArray[legIndex];
    const rawCoords = legData.waypoints.map(w => [w.lon, w.lat]);
    legData.routeJson = {
      type: "FeatureCollection",
      features: [{ 
        type: "Feature",
        geometry: { type: "LineString", coordinates: rawCoords },
        properties: { source: "custom_curated" }
      }]
    };
    if (legData.polylineLayer) {
        const latlngs = legData.waypoints.map(w => [w.lat, w.lon] as [number, number]);
        legData.polylineLayer.setLatLngs(latlngs);
    }
    if (redrawMarkers) {
      this.drawAllLegs(false);
    }
    this.cdr.detectChanges();
  }
  
  saveAll() {
    this.isSaving = true;
    let total = this.legDataArray.length;
    let completed = 0;
    
    if (total === 0) {
      this.dialogRef.close();
      return;
    }
    
    for (const legData of this.legDataArray) {
      // Sync back to original parent object so it persists when re-opening
      if (legData.originalSeg) {
        legData.originalSeg.route = legData.routeJson;
        legData.originalSeg.waypoints = legData.waypoints.filter(w => w.isNoon);
      }
      
      this.requestFormService.saveRoute(legData.route_id, legData.routeJson).subscribe({
        next: () => {
          completed++;
          if (completed === total) {
            this.isSaving = false;
            this.dialogRef.close({ saved: true });
          }
        },
        error: (err) => {
          console.error('Error saving route', err);
          completed++;
          if (completed === total) {
            this.isSaving = false;
            this.dialogRef.close({ saved: true });
          }
        }
      });
    }
  }
  
  close() {
    this.dialogRef.close();
  }
}
